import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useNavigate,
  Navigate,
  useLocation,
} from "react-router-dom";

import "./App.css";

// Pages
import IncidentQueuePage from "./pages/IncidentQueuePage";
import SOCOperationsPage from "./pages/SOCOperationsPage";
import IncidentDetailsPage from "./pages/IncidentDetailsPage";
import AIDiscovery from "./pages/AIDiscovery";
import DashboardHome from "./pages/DashboardHome";
import DevicesPage from "./pages/DevicesPage";
import LogsPage from "./pages/LogsPage";
import PoliciesPage from "./pages/PoliciesPage";
import DeviceDetailsPage from "./pages/DeviceDetailsPage";
import EDRResponseCenterPage from "./pages/EDRResponseCenterPage";
import LoginPage from "./pages/LoginPage";
import AUPPage from "./pages/AUPPage";
import ClientDashboardPage from "./pages/ClientDashboardPage";
import AdminSettingsPage from "./pages/AdminSettingsPage";
import VulnerabilitiesPage from "./pages/VulnerabilitiesPage";
import NetworkProtection from "./NetworkProtection";
import NetworkDiscoveryPage from "./pages/NetworkDiscoveryPage";
import PenTestPage from "./pages/PenTestPage";

import SOCAnalystConsolePage from "./pages/SOCAnalystConsolePage";
import ThreatIntelligencePage from "./pages/ThreatIntelligencePage";
import ThreatIndicatorsPage from "./pages/ThreatIndicatorsPage";
import ThreatFeedsPage from "./pages/ThreatFeedsPage";
import ThreatAnalyticsPage from "./pages/ThreatAnalyticsPage";
import ReportsCenterPage from "./pages/ReportsCenterPage";
import ComplianceCenterPage from "./pages/ComplianceCenterPage";
import ComplianceAssessmentPage from "./pages/ComplianceAssessmentPage";
import RiskRegisterPage from "./pages/RiskRegisterPage";
import ExecutiveGRCPage from "./pages/ExecutiveGRCPage";
import IdentityZeroTrustPage from "./pages/IdentityZeroTrustPage";
import UEBAPage from "./pages/UEBAPage";
import ThreatHuntingPage from "./pages/ThreatHuntingPage";
import AssetInventoryPage from "./pages/AssetInventoryPage";
import AssetTopologyPage from "./pages/AssetTopologyPage";
import AssetDetailsPage from "./pages/AssetDetailsPage";
import RemoteUninstallPage from "./pages/RemoteUninstallPage";
import DeviceHealthPage from "./pages/DeviceHealthPage";
import PolicyLifecyclePage from "./pages/PolicyLifecyclePage";
import PolicyAssignmentsPage from "./pages/PolicyAssignmentsPage";
import PolicyEnforcementPage from "./pages/PolicyEnforcementPage";
import PolicyPlatformControlsPage from "./pages/PolicyPlatformControlsPage";
import EndpointInventoryPage from "./pages/EndpointInventoryPage";
import RuntimeTelemetryPage from "./pages/RuntimeTelemetryPage";
import TelemetryGovernancePage from "./pages/TelemetryGovernancePage";
import OverviewV2Page from "./pages/OverviewV2Page";
import TenantEnrollmentPage from "./pages/TenantEnrollmentPage";
import PolicyTruthPage from "./pages/PolicyTruthPage";
// =========================
// Private Route
// =========================
function PrivateRoute({ children, loggedIn, aupAccepted }) {
  if (!loggedIn) {
    return <Navigate to="/login" replace />;
  }

  if (!aupAccepted) {
    return <Navigate to="/aup" replace />;
  }

  return children;
}

// =========================
// Dashboard Layout
// =========================
function DashboardShell({ setLoggedIn }) {
  const navigate = useNavigate();
  const shellLocation = useLocation();
  const preserveLegacyDashboard =
    shellLocation.pathname === "/dashboard";

  const handleLogout = () => {
    localStorage.removeItem("loggedIn");
    localStorage.removeItem("access_token");
    localStorage.removeItem("admin_id");
    localStorage.removeItem("username");
    localStorage.removeItem("role");
    localStorage.removeItem("company_id");
    localStorage.removeItem("aupAccepted");

    setLoggedIn(false);
    navigate("/login");
  };

  return (
    <div className="dashboard-layout">
      <aside className="sidebar">
        <div className="brand-box">
          <img src="/logo.png" alt="Wireless Press" className="logo-image" />
          <div>
            <h2 className="brand-title">Wireless Press</h2>
            <p className="brand-subtitle">Lockdown Console</p>
          </div>
        </div>

        {preserveLegacyDashboard ? (
          <nav className="nav-menu">
            <Link to="/dashboard" className="nav-link">
              Overview
            </Link>

            <Link to="/assets" className="nav-link">
              Asset Inventory
            </Link>

            <Link to="/assets/topology" className="nav-link">
              Asset Topology
            </Link>

            <Link to="/devices" className="nav-link">
              Devices
            </Link>

            <Link to="/remote-uninstall" className="nav-link">
              Remote Uninstall
            </Link>

            <Link to="/device-health" className="nav-link">
              Device Health
            </Link>

            <Link to="/edr-response" className="nav-link">
              EDR Response Center
            </Link>

            <Link to="/policies" className="nav-link">
              Policies
            </Link>

            <Link to="/policy-lifecycle" className="nav-link">
              Policy Lifecycle
            </Link>

            <Link to="/policy-assignments" className="nav-link">
              Policy Assignments
            </Link>

            <Link to="/policy-enforcement" className="nav-link">
              Policy Enforcement
            </Link>

            <Link to="/policy-platform-controls" className="nav-link">
              Platform Controls
            </Link>

            <Link to="/endpoint-inventory" className="nav-link">
              Endpoint Inventory
            </Link>

            <Link to="/runtime-telemetry" className="nav-link">
              Runtime Telemetry
            </Link>

            <Link to="/telemetry-governance" className="nav-link">
              Telemetry Governance
            </Link>

            <Link to="/settings" className="nav-link">
              Settings
            </Link>

            <Link to="/logs" className="nav-link">
              Logs
            </Link>

            <Link to="/soc-console" className="nav-link">
              SOC Analyst Console
            </Link>

            <Link to="/incidents" className="nav-link">
              SOC Incidents
            </Link>

            <Link to="/soc-operations" className="nav-link">
              SOC Operations
            </Link>

            <Link to="/vulnerabilities" className="nav-link">
              Vulnerabilities
            </Link>

            <Link to="/pentest" className="nav-link">
              Pen Test
            </Link>

            <Link to="/threat-intelligence" className="nav-link">
              Threat Intelligence
            </Link>

            <Link
              to="/threat-intelligence/indicators"
              className="nav-link"
            >
              IOC Explorer
            </Link>

            <Link
              to="/threat-intelligence/feeds"
              className="nav-link"
            >
              Threat Feeds
            </Link>

            <Link
              to="/threat-intelligence/analytics"
              className="nav-link"
            >
              Threat Analytics
            </Link>

            <Link to="/reports-center" className="nav-link">
              Reports Center
            </Link>

            <Link to="/compliance-center" className="nav-link">
              Compliance Center
            </Link>

            <Link to="/risk-register" className="nav-link">
              Risk Register
            </Link>

            <Link to="/executive-grc" className="nav-link">
              Executive GRC
            </Link>

            <Link to="/identity-zero-trust" className="nav-link">
              Identity & Zero Trust
            </Link>

            <Link to="/ueba" className="nav-link">
              UEBA
            </Link>

            <Link to="/threat-hunting" className="nav-link">
              Threat Hunting
            </Link>

            <Link to="/network-protection" className="nav-link">
              Network Protection
            </Link>

            <Link to="/network-discovery" className="nav-link">
              Network Discovery
            </Link>
          </nav>
        ) : (
          <nav className="nav-menu wp-nav-consolidated">

            <Link
              to="/overview-v2"
              className="nav-link wp-nav-overview"
            >
              Overview
            </Link>

            <details className="wp-nav-group" open>
              <summary>Devices</summary>

              <div className="wp-nav-group-items">
                <a
                  href="/devices"
                  className="nav-link"
                  onClick={() => {
                    localStorage.setItem(
                      "wp_scope_platform",
                      "all"
                    );
                    localStorage.removeItem(
                      "wp_scope_device_uuid"
                    );
                    localStorage.removeItem(
                      "wp_scope_device_id"
                    );
                  }}
                >
                  All Devices
                </a>

                <a
                  href="/devices"
                  className="nav-link"
                  onClick={() => {
                    localStorage.setItem(
                      "wp_scope_platform",
                      "windows"
                    );
                    localStorage.removeItem(
                      "wp_scope_device_uuid"
                    );
                    localStorage.removeItem(
                      "wp_scope_device_id"
                    );
                  }}
                >
                  Windows
                </a>

                <a
                  href="/devices"
                  className="nav-link"
                  onClick={() => {
                    localStorage.setItem(
                      "wp_scope_platform",
                      "macos"
                    );
                    localStorage.removeItem(
                      "wp_scope_device_uuid"
                    );
                    localStorage.removeItem(
                      "wp_scope_device_id"
                    );
                  }}
                >
                  macOS
                </a>

                <a
                  href="/devices"
                  className="nav-link"
                  onClick={() => {
                    localStorage.setItem(
                      "wp_scope_platform",
                      "android"
                    );
                    localStorage.removeItem(
                      "wp_scope_device_uuid"
                    );
                    localStorage.removeItem(
                      "wp_scope_device_id"
                    );
                  }}
                >
                  Android
                </a>

                <a
                  href="/devices"
                  className="nav-link"
                  onClick={() => {
                    localStorage.setItem(
                      "wp_scope_platform",
                      "ios"
                    );
                    localStorage.removeItem(
                      "wp_scope_device_uuid"
                    );
                    localStorage.removeItem(
                      "wp_scope_device_id"
                    );
                  }}
                >
                  iOS / iPadOS
                </a>

                <Link to="/devices" className="nav-link">
                  Kiosk Devices
                </Link>

                <Link
                  to="/device-health"
                  className="nav-link"
                >
                  Device Health
                </Link>

                <Link
                  to="/endpoint-inventory"
                  className="nav-link"
                >
                  Endpoint Inventory
                </Link>

                <Link
                  to="/remote-uninstall"
                  className="nav-link"
                >
                  Remote Uninstall
                </Link>

                <Link
                  to="/tenant-enrollment"
                  className="nav-link"
                >
                  Tenant Enrollment
                </Link>
              </div>
            </details>

            <details className="wp-nav-group" open>
              <summary>Protection</summary>

              <div className="wp-nav-group-items">
                <Link
                  to="/edr-response"
                  className="nav-link"
                >
                  EDR Response
                </Link>

                <Link
                  to="/policies"
                  className="nav-link"
                >
                  Policies
                </Link>

                <Link
                  to="/policy-lifecycle"
                  className="nav-link"
                >
                  Policy Lifecycle
                </Link>

                <Link
                  to="/policy-assignments"
                  className="nav-link"
                >
                  Policy Assignments
                </Link>

                <Link
                  to="/policy-enforcement"
                  className="nav-link"
                >
                  Policy Enforcement
                </Link>

                <Link
                  to="/policy-platform-controls"
                  className="nav-link"
                >
                  Platform Controls
                </Link>

                <Link
                  to="/policy-truth"
                  className="nav-link"
                >
                  Policy Truth
                </Link>

                <Link
                  to="/vulnerabilities"
                  className="nav-link"
                >
                  Vulnerabilities
                </Link>

                <Link
                  to="/network-protection"
                  className="nav-link"
                >
                  Network Protection
                </Link>

                <Link
                  to="/network-discovery"
                  className="nav-link"
                >
                  Network Discovery
                </Link>
              </div>
            </details>

            <details className="wp-nav-group">
              <summary>Telemetry</summary>

              <div className="wp-nav-group-items">
                <Link
                  to="/runtime-telemetry"
                  className="nav-link"
                >
                  Runtime Telemetry
                </Link>

                <Link to="/logs" className="nav-link">
                  Logs
                </Link>

                <Link
                  to="/telemetry-governance"
                  className="nav-link"
                >
                  Telemetry Governance
                </Link>

                <Link to="/assets" className="nav-link">
                  Asset Inventory
                </Link>

                <Link
                  to="/assets/topology"
                  className="nav-link"
                >
                  Asset Topology
                </Link>
              </div>
            </details>

            <details className="wp-nav-group">
              <summary>Threat Operations</summary>

              <div className="wp-nav-group-items">
                <Link
                  to="/soc-console"
                  className="nav-link"
                >
                  SOC Analyst Console
                </Link>

                <Link
                  to="/incidents"
                  className="nav-link"
                >
                  SOC Incidents
                </Link>

                <Link
                  to="/soc-operations"
                  className="nav-link"
                >
                  SOC Operations
                </Link>

                <Link
                  to="/threat-intelligence"
                  className="nav-link"
                >
                  Threat Intelligence
                </Link>

                <Link
                  to="/threat-intelligence/indicators"
                  className="nav-link"
                >
                  IOC Explorer
                </Link>

                <Link
                  to="/threat-intelligence/feeds"
                  className="nav-link"
                >
                  Threat Feeds
                </Link>

                <Link
                  to="/threat-intelligence/analytics"
                  className="nav-link"
                >
                  Threat Analytics
                </Link>

                <Link
                  to="/threat-hunting"
                  className="nav-link"
                >
                  Threat Hunting
                </Link>

                <Link
                  to="/ai-discovery"
                  className="nav-link"
                >
                  AI Discovery
                </Link>

                <Link
                  to="/pentest"
                  className="nav-link"
                >
                  Pen Test
                </Link>
              </div>
            </details>

            <details className="wp-nav-group">
              <summary>Governance</summary>

              <div className="wp-nav-group-items">
                <Link
                  to="/reports-center"
                  className="nav-link"
                >
                  Reports Center
                </Link>

                <Link
                  to="/compliance-center"
                  className="nav-link"
                >
                  Compliance Center
                </Link>

                <Link
                  to="/risk-register"
                  className="nav-link"
                >
                  Risk Register
                </Link>

                <Link
                  to="/executive-grc"
                  className="nav-link"
                >
                  Executive GRC
                </Link>

                <Link
                  to="/identity-zero-trust"
                  className="nav-link"
                >
                  Identity & Zero Trust
                </Link>

                <Link
                  to="/ueba"
                  className="nav-link"
                >
                  UEBA
                </Link>
              </div>
            </details>

            <details className="wp-nav-group">
              <summary>Settings</summary>

              <div className="wp-nav-group-items">
                <Link
                  to="/settings"
                  className="nav-link"
                >
                  Admin Settings
                </Link>

                <Link
                  to="/tenant-enrollment"
                  className="nav-link"
                >
                  Tenant Enrollment
                </Link>
              </div>
            </details>

          </nav>
        )}
      </aside>

      <main className="main-panel">
        <header className="topbar">
          <div>
            <h1 className="page-title">Wireless Press Dashboard</h1>
            <p className="page-subtitle">
              Device Management & Kiosk Control Platform by George Taylor
            </p>
          </div>

          <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
            <div className="topbar-badge">Admin Console</div>

            <button
              onClick={handleLogout}
              style={{ padding: "10px 16px", cursor: "pointer" }}
            >
              Logout
            </button>
          </div>
        </header>

        <section className="content-area">
          <Routes>
            
              <Route
                path="/soc-console"
                element={<SOCAnalystConsolePage />}
              />
<Route path="/incidents" element={<IncidentQueuePage />} />
              <Route
                path="/incidents/:id"
                element={<IncidentDetailsPage />}
              />
              <Route
                path="/soc-operations"
                element={<SOCOperationsPage />}
              />
            <Route path="/dashboard" element={<DashboardHome />} />} />
            <Route path="/devices" element={<DevicesPage />} />
          <Route path="/remote-uninstall" element={<RemoteUninstallPage />} />
          <Route path="/device-health" element={<DeviceHealthPage />} />
            <Route
              path="/edr-response"
              element={<EDRResponseCenterPage />}
            />
            <Route path="/device/:id" element={<DeviceDetailsPage />} />
            <Route path="/policies" element={<PoliciesPage />} />
          <Route path="/policy-lifecycle" element={<PolicyLifecyclePage />} />
          <Route path="/policy-assignments" element={<PolicyAssignmentsPage />} />
          <Route path="/policy-enforcement" element={<PolicyEnforcementPage />} />
          <Route path="/policy-platform-controls" element={<PolicyPlatformControlsPage />} />
          <Route path="/endpoint-inventory" element={<EndpointInventoryPage />} />
          <Route path="/runtime-telemetry" element={<RuntimeTelemetryPage />} />
          <Route path="/telemetry-governance" element={<TelemetryGovernancePage />} />
            <Route path="/logs" element={<LogsPage />} />
            <Route path="/settings" element={<AdminSettingsPage />} />
            <Route path="/vulnerabilities" element={<VulnerabilitiesPage />} />
            <Route path="/network-protection" element={<NetworkProtection />} />
            <Route path="/network-discovery" element={<NetworkDiscoveryPage />} />
            <Route path="/ai-discovery" element={<AIDiscovery />} />
            <Route path="/pentest" element={<PenTestPage />} />
              <Route
                path="/threat-intelligence"
                element={<ThreatIntelligencePage />}
              />
              <Route
                path="/threat-intelligence/indicators"
                element={<ThreatIndicatorsPage />}
              />
              <Route
                path="/threat-intelligence/feeds"
                element={<ThreatFeedsPage />}
              />
              <Route
                path="/threat-intelligence/analytics"
                element={<ThreatAnalyticsPage />}
              />
              <Route
                path="/reports-center"
                element={<ReportsCenterPage />}
              />
              <Route
                path="/compliance-center"
                element={<ComplianceCenterPage />}
              />
              <Route
                path="/compliance/assessments/:assessmentId"
                element={<ComplianceAssessmentPage />}
              />
              <Route
                path="/risk-register"
                element={<RiskRegisterPage />}
              />
              <Route
                path="/executive-grc"
                element={<ExecutiveGRCPage />}
              />
              <Route
                path="/identity-zero-trust"
                element={<IdentityZeroTrustPage />}
              />
              <Route
                path="/assets"
                element={<AssetInventoryPage />}
              />
              <Route
                path="/assets/topology"
                element={<AssetTopologyPage />}
              />
              <Route
                path="/assets/:assetId"
                element={<AssetDetailsPage />}
              />
              <Route
                path="/ueba"
                element={<UEBAPage />}
              />
              <Route
                path="/threat-hunting"
                element={<ThreatHuntingPage />}
              />



















            <Route
              path="/overview-v2"
              element={<OverviewV2Page />}
            />

            <Route path="/tenant-enrollment" element={<TenantEnrollmentPage />} />

            <Route path="/policy-truth" element={<PolicyTruthPage />} />

            <Route path="*" element={<Navigate to="/dashboard" replace />} />
</Routes>
        </section>
      </main>
    </div>
  );
}

// =========================
// Route Controller
// =========================
function AppRoutes() {
  const [loggedIn, setLoggedIn] = useState(() => {
    const session = localStorage.getItem("loggedIn");
    const token = localStorage.getItem("access_token");
    return session === "true" && Boolean(token);
  });

  const [aupAccepted, setAupAccepted] = useState(
    () => localStorage.getItem("aupAccepted") === "true"
  );
  const location = useLocation();

  useEffect(() => {
    const session = localStorage.getItem("loggedIn");
    const accepted = localStorage.getItem("aupAccepted");
    const token = localStorage.getItem("access_token");

    if (session === "true" && !token) {
      localStorage.clear();
      setLoggedIn(false);
      setAupAccepted(false);
      return;
    }

    setLoggedIn(session === "true");
    setAupAccepted(accepted === "true");
  }, [location.pathname]);

  const role = localStorage.getItem("role");

  if (location.pathname === "/login") {
    if (!loggedIn) {
      return <LoginPage setLoggedIn={setLoggedIn} />;
    }

    if (!aupAccepted) {
      return <Navigate to="/aup" replace />;
    }

    return role === "super_admin" ? (
      <Navigate to="/dashboard" replace />
    ) : (
      <Navigate to="/client-dashboard" replace />
    );
  }

  if (location.pathname === "/aup") {
    if (!loggedIn) {
      return <Navigate to="/login" replace />;
    }

    if (aupAccepted) {
      return role === "super_admin" ? (
        <Navigate to="/dashboard" replace />
      ) : (
        <Navigate to="/client-dashboard" replace />
      );
    }

    return <AUPPage />;
  }

  if (location.pathname === "/client-dashboard") {
    if (role === "super_admin") {
      return <Navigate to="/dashboard" replace />;
    }

    return (
      <PrivateRoute loggedIn={loggedIn} aupAccepted={aupAccepted}>
        <ClientDashboardPage />
      </PrivateRoute>
    );
  }

  if (role !== "super_admin") {
    return <Navigate to="/client-dashboard" replace />;
  }

  return (
    <PrivateRoute loggedIn={loggedIn} aupAccepted={aupAccepted}>
      <DashboardShell setLoggedIn={setLoggedIn} />
    </PrivateRoute>
  );
}

// =========================
// App Root
// =========================
function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
